import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { PageHeader } from "./fordon";
import { HubEditorial, faqSchema } from "@/components/HubEditorial";
import { DeferredIsland } from "@/components/DeferredIsland";
import { getCalibrations } from "@/lib/valuation-calibration.functions";

import {
  VARDERING_AUTHOR,
  VARDERING_UPDATED,
  varderingFaq,
  varderingSections,
  varderingTldr,
} from "@/data/vardering";
import hero from "@/assets/guider/vardera-husbil.jpg";

const loadValuationTool = () => import("@/components/islands/ValuationToolIsland");

const URL = "https://www.akhusbilfritid.se/vardera-husbil";
const TITLE = "Värdera husbil & husvagn – gratis verktyg med öppen räkning";
const DESC =
  "Räkna fram vad din husbil eller husvagn är värd på 30 sekunder. Se varje faktor, jämför marknadspris, förmedlingsnetto och inbyte.";

export const Route = createFileRoute("/vardera-husbil")({
  loader: async () => ({ calibrations: await getCalibrations() }),
  head: () => ({

    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      {
        name: "keywords",
        content:
          "värdera husbil, värdera husvagn, vad är min husbil värd, husbilsvärdering, värdering husvagn online, inbyte husbil",
      },
      { property: "og:type", content: "website" },
      { property: "og:title", content: "Värdera husbil & husvagn — gratis, med öppen räkning" },
      {
        property: "og:description",
        content:
          "Ett värderingsverktyg som visar hela räkningen: ålder, fabrikat, mil, skick, fukt och säsong. Plus skriftlig värdering från verkstaden.",
      },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "@id": `${URL}#app`,
          name: "Värderingsverktyg för husbil och husvagn",
          url: URL,
          applicationCategory: "FinanceApplication",
          operatingSystem: "Web",
          inLanguage: "sv-SE",
          offers: { "@type": "Offer", price: "0", priceCurrency: "SEK" },
          provider: { "@type": "Organization", name: "AK Husbil & Fritid" },
        }),
      },
      { type: "application/ld+json", children: JSON.stringify(faqSchema(varderingFaq)) },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Hem", item: "https://www.akhusbilfritid.se/" },
            { "@type": "ListItem", position: 2, name: "Guider", item: "https://www.akhusbilfritid.se/guider" },
            { "@type": "ListItem", position: 3, name: "Värdera husbil", item: URL },
          ],
        }),
      },
    ],
  }),
  component: Page,
});

const facts = [
  { k: "30 sek", v: "till ett intervall — utan att lämna e-post" },
  { k: "8 faktorer", v: "ålder, fabrikat, mil, längd, skick, fukt, säsong, extra" },
  { k: "3 tal", v: "marknadspris, förmedlingsnetto och inbyte" },
  { k: "0 kr", v: "skriftlig värdering från verkstaden i Kalix" },
];

function Page() {
  const { calibrations } = Route.useLoaderData();
  return (
    <>
      <PageHeader
        label="Verktyg · Värdering"
        title={
          <>
            Vad är din husbil värd? <span className="font-italic-display text-accent">Hela räkningen</span> syns.
          </>
        }
        sub="Ett värderingsverktyg som inte gömmer metoden. Fyll i fordonet, se varje multiplikator, och jämför vad privat annons, förmedling och inbyte ger i handen."
      />

      <section className="container-edge pt-8 md:pt-12">
        <DeferredIsland
          load={loadValuationTool}
          componentProps={{ calibrations }}
          minHeight={620}
          label="Laddar värderingsverktyget…"
        />
      </section>


      <section className="container-edge pt-12 md:pt-16">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {facts.map((f) => (
            <div key={f.k} className="rounded-3xl border border-border bg-card p-6">
              <p className="font-display text-3xl leading-none text-accent">{f.k}</p>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.v}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="container-edge pt-12 md:pt-16">
        <figure className="overflow-hidden rounded-3xl border border-border">
          <img
            src={hero}
            alt="Tekniker mäter fuktvärden på en vit husbil i verkstaden i Kalix inför värdering"
            width={1600}
            height={912}
            loading="lazy"
            decoding="async"
            className="h-auto w-full object-cover"
          />
          <figcaption className="border-t border-border bg-card px-5 py-3 text-xs text-muted-foreground">
            Fuktmätning i verkstaden i Kalix — det enskilt största beslutet i varje värdering.
          </figcaption>
        </figure>
      </section>

      <div className="pt-14 md:pt-20">
        <HubEditorial
          eyebrow="Metoden"
          sections={varderingSections}
          faq={varderingFaq}
          tldr={varderingTldr}
          updated={VARDERING_UPDATED}
          author={VARDERING_AUTHOR}
        />
      </div>

      <section className="container-edge pb-20 md:pb-24">
        <div className="rounded-3xl border border-border bg-warm p-7 md:p-10">
          <p className="eyebrow">Läs vidare</p>
          <div className="mt-5 grid gap-3 sm:grid-cols-3">
            <Link
              to="/salja-husbil"
              className="group flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-5 transition hover:border-accent"
            >
              <span className="text-sm font-medium">Sälja husbil — förmedling, inbyte eller privat?</span>
              <ArrowUpRight size={16} className="shrink-0 text-accent transition group-hover:translate-x-0.5" />
            </Link>
            <Link
              to="/fukttest-husbil"
              className="group flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-5 transition hover:border-accent"
            >
              <span className="text-sm font-medium">Fukttest — gränsvärden och vad skador kostar</span>
              <ArrowUpRight size={16} className="shrink-0 text-accent transition group-hover:translate-x-0.5" />
            </Link>
            <Link
              to="/salda"
              className="group flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-5 transition hover:border-accent"
            >
              <span className="text-sm font-medium">Arkivet: slutpriser på sålda fordon</span>
              <ArrowUpRight size={16} className="shrink-0 text-accent transition group-hover:translate-x-0.5" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
